import {
  resolveAuthUserUrl,
  resolveGatewayService,
  resolveRpaGateway,
} from "../utils/gatewayEnv";

/**
 * Ingress first-path segments for one-risa services on the public API gateway
 * (same names as k8s ingress / ``GKEClusterEndpoints`` URL paths).
 */
const APIS_GW_SERVICE_PATH = {
  PA_ORDER_CREATION: "pa-order-creation",
  PHARMACY_PA_ORDER_CREATION: "pharmacy-pa-order-creation",
  PHARMACY_RPA_SERVICE: "pharmacy-rpa-service",
} as const;

/**
 * Resolves the browser-facing base URL for a service mounted under ``apis`` / ``apis-dev`` / local gateway.
 *
 * @param ingressPath - First URL path segment without slashes (e.g. ``pa-order-creation``)
 * @returns Base URL with trailing slash
 */
function apisGatewayServiceBaseUrl(ingressPath: string): string {
  const segment = ingressPath.replace(/^\/+|\/+$/g, "");
  return resolveGatewayService(
    segment,
    `https://apis.risalabs.ai/${segment}/`,
    `https://apis-dev.risalabs.ai/${segment}/`,
  );
}

const RPA_SERVICE_CLOUDRUN = `${resolveRpaGateway()}/rpa-service/`;

const EV_BV_SERVICE = resolveGatewayService(
  "backend-service/medical",
  "https://apis.risalabs.ai/backend-service/medical/",
  "https://apis-dev.risalabs.ai/backend-service/medical/",
);

const PA_ORDER_CREATION_SERVICE = apisGatewayServiceBaseUrl(
  APIS_GW_SERVICE_PATH.PA_ORDER_CREATION,
);

/** Base URL for pharmacy-pa-order-creation (distinct from medical ``pa-order-creation``). */
const PHARMACY_PA_ORDER_CREATION_SERVICE = apisGatewayServiceBaseUrl(
  APIS_GW_SERVICE_PATH.PHARMACY_PA_ORDER_CREATION,
);

/** one-risa pharmacy RPA (NYCBS, CMM, OncoEMR write-back). */
const PHARMACY_RPA_SERVICE = apisGatewayServiceBaseUrl(
  APIS_GW_SERVICE_PATH.PHARMACY_RPA_SERVICE,
);

/** @deprecated Use ``PHARMACY_RPA_SERVICE`` — kept for call sites migrating off ``/rpa-service/v1``. */
const RPA_SERVICE_KUBERNATES = PHARMACY_RPA_SERVICE;

/** RPA pharmacy PA submission (Astera case initiation, OncoEMR re-run, etc.). */
const RPA_NYCBS_PHARMA_SUBMIT_PA = `${PHARMACY_RPA_SERVICE}submit-pa`;

const BACKEND_SERVICE = resolveGatewayService(
  "backend-service",
  "https://apis.risalabs.ai/backend-service/",
  "https://apis-dev.risalabs.ai/backend-service/",
);

const BASE_URL = resolveGatewayService("", "https://apis.risalabs.ai/", "https://apis-dev.risalabs.ai/");

const AUTH_URL = resolveAuthUserUrl();

export const ApiBaseUrls = {
  AUTH_URL: AUTH_URL,
  EV_BV_SERVICE: EV_BV_SERVICE,
  LOCAL_HOST: "http://127.0.0.1:8000/rpa-service/",
  RPA_SERVICE_CLOUDRUN: RPA_SERVICE_CLOUDRUN,
  RPA_SERVICE_KUBERNATES: RPA_SERVICE_KUBERNATES,
  PHARMACY_RPA_SERVICE: PHARMACY_RPA_SERVICE,
  PA_ORDER_CREATION_SERVICE: PA_ORDER_CREATION_SERVICE,
  PHARMACY_PA_ORDER_CREATION_SERVICE: PHARMACY_PA_ORDER_CREATION_SERVICE,
  BACKEND_SERVICE: BACKEND_SERVICE,
  BASE_URL: BASE_URL,
  PROXY_BASE_URL:
    "https://us-central1-prior--backen-prod-svc-u4g8.cloudfunctions.net/http-proxy",
  DEV_SUBMI_PA_FORM:
    "https://apis.risalabs.ai/prior-auth-submission/api/v1/pa-submission/submit-pa-form",
  PROD_SUBMI_PA_FORM:
    "https://apis.risalabs.ai/prior-auth-submission/api/v1/pa-submission/submit-pa-form",
} as const;

export const API_ENDPOINTS = {
  PHARMACY_TASKS_WORKLIST: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/tasks/worklist`,
  PHARMACY_TASKS_FILTER_OPTIONS: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/tasks/filter-options`,
  PHARMACY_TASK_DETAIL: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/tasks`,
  PHARMACY_TASK_REPLY: `${PHARMACY_PA_ORDER_CREATION_SERVICE}astera-primary-care/tasks/reply`,
  PHARMACY_TASK_STATUS: `${PHARMACY_PA_ORDER_CREATION_SERVICE}astera-primary-care/tasks/status`,
  PHARMACY_TASK_ADD_RECIPIENT: `${PHARMACY_PA_ORDER_CREATION_SERVICE}astera-primary-care/tasks/add-recipient`,
  PHARMACY_TASK_REMOVE_RECIPIENT: `${PHARMACY_PA_ORDER_CREATION_SERVICE}astera-primary-care/tasks/remove-recipient`,
  PHARMACY_ONCOEMR_USERS_SEARCH: `${ApiBaseUrls.BACKEND_SERVICE}oncoemr-tasks/users/search`,
  PHARMACY_TASK_WRITEBACK_RETRY: `${PHARMACY_PA_ORDER_CREATION_SERVICE}astera-primary-care/tasks/writeback/retry`,
  GET_UNASSIGNED_ORDERS: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}nycbs_bg_query/get-unassigned-cmm-orders`,
  GET_ASSIGNED_ORDERS: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}nycbs_bg_query/get-assigned-cmm-orders-with-status`,
  UPDATE_CMM_TABLE: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}nycbs_bg_query/update-cmm-table`,
  GET_SINGLE_ORDER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}get-single-order-details`,
  GET_ALL_QUESTIONS_FOR_ORDER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}questionnaire-data`,
  GET_ALL_DOCUMENTS_FOR_ORDER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}documents`,
  UPDATE_QUESTIONAIRE_RESPONSE: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}v1/edit-cover-my-meds-questionnaire`,
  GET_UNIQUE_CMM_ORDERS: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}get-unique-cmm-orders`,
  EDIT_COVER_MY_MEDS_REQUEST: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}edit-cmm-data`,
  SEARCH_IN_CMM_INPUT_TABLE: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}search-in-cmm-table`,
  GET_PRESCRIPTION_DATA: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}prescription-data`,
  GET_ALL_INSURANCE_DATA_FOR_ORDER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}get-insurance-details`,
  SEND_TO_PLAN_IN_CMM: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}send-to-plan`,
  GET_ALL_DIAGNOSIS_CODES_FOR_ORDER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}get-diagnosis-details`,
  EV_BV_FROM_EXCEL: `${ApiBaseUrls.EV_BV_SERVICE}coverage/check-coverage-for-csv`,
  GET_EV_BV_REPORT_DETAILS: `${ApiBaseUrls.EV_BV_SERVICE}coverage/prepare-coverage-excl`,
  CHECK_CLAIMS_FOR_CSV: `${ApiBaseUrls.BACKEND_SERVICE}commons/claims/check-claims-for-csv`,
  PREPARE_CLAIMS_EXCEL: `${ApiBaseUrls.BACKEND_SERVICE}commons/claims/prepare-claims-excl`,
  CHECK_AVAILITY_PAYER_ID: `${ApiBaseUrls.BACKEND_SERVICE}commons/availity/check-availity-payer-id`,
  GET_PATIENT_ELIGIBILITY_DETAILS: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}get-patient-eligibility-details`,
  DELETE_CMM_ORDER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}delete-entry-on-cmm`,
  GET_INTERNAL_PROCESSED_ORDER_STATUS: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}get-processed-pa-orders`,
  RE_RUN_ONCO_EMR_WORKFLOW: RPA_NYCBS_PHARMA_SUBMIT_PA,
  APPEND_ROW_TO_GSHEET: `${ApiBaseUrls.BACKEND_SERVICE}commons/gsheet/append-data-to-specified-sheet`,
  AUTH_ON_FILE_CHECK: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}v1/utility_apis/auth-on-file-check`,
  RUN_STATUS_TRACKING: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}run_status_tracking`,
  BEARER_TOKEN_URL: `${ApiBaseUrls.AUTH_URL}/token`,
  CHECK_RATE_LIMIT_PASSWORD_RESET: `${ApiBaseUrls.AUTH_URL}/check-rate-limit-password-reset`,
  UPDATE_CIG_BOX_DATA: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}v1/utility_apis/write_note_oncoemr`,
  MEDICAL_PA_SUBMISSION: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/submission-medical-pa/submit-medical-pa-order`,
  FETCH_PORTAL_PASSWORD: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/fetch-password`,
  ADD_EDIT_PORTAL_CREDENTIAL: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}commons/portal/add-credential`,
  REFETCH_CLINICAL_ATTACHMENT: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}refetch-clinical-attachment?identifier=Identifier`,
  PROCESS_CSV_PHARMACY_PA: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/process-csv`,
  GET_FINAL_WORKLIST_DATA: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/get-data`,
  GET_MEDICAL_PA_ANALYTICS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/external-worklist/get-analytics`,
  FETCH_NCCN_GUIDELINES: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/fetch-guidelines`,
  UPDATE_J_CODES: `${ApiBaseUrls.RPA_SERVICE_CLOUDRUN}v1/utility_apis/update-j-codes`,
  SEND_INVITE_VIA_EMAIL: `${ApiBaseUrls.AUTH_URL}/invite-user-to-dashboard`,
  DOWNLOAD_AUDIT_TRAIL: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}utility_apis/download-audit-trail`,
  DETERMINE_ICD_CODE: `${ApiBaseUrls.BASE_URL}medical-necessity/v1/nycbs/determine-icd-code-llm`,
  UPLOAD_AUTH_LETTER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}upload_authorization_letter`,
  POST_STATUS_TRACKING: `${ApiBaseUrls.BASE_URL}ai-service/pharmacy/astera/push-orders`,
  LOG_EVENT_ENDPOINT: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/log-frontend-event`,
  AUDIT_TRIAL_OVERVIEW: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-audit-trail-overview`,
  GET_AUDIT_TRIAL_DETAILS: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-detailed-audit-trail`,
  DETERMINE_ICD_CODE_LLM: `${ApiBaseUrls.BASE_URL}ai-service/commons/icd-code-detect/determine-icd-code-llm`,
  VIEW_ALL_AUDIT_TRAIL: `${ApiBaseUrls.BACKEND_SERVICE}commons/audit-trail/get-audit-trail`,
  ALGOLIA_SEARCH: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-search`,
  PREPARE_FORM_QUESTIONNAIRE: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/prepare-form-questionnaire`,
  SUBMIT_FAX_FORM_QUESTIONNAIRE: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/submit`,
  COMMENT_RPA_MODAL_SAVE: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}utility_apis/mark-as-completed`,
  DETERMINE_CLINICAL_QUESTIONNAIRE_LLM: `${ApiBaseUrls.BASE_URL}ai-service/commons/medical-necessity/get-medical-necessity-llm`,
  ALGOLIA_FACET_COUNTS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-facet-counts`,
  ALGOLIA_MULTI_FACET_COUNTS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/utility/algolia-multi-facet-counts`,
  NAR_RPA_TRIGGER: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}rpa/availity-healthfirst-checknar`,
  FETCH_AUTH_GRID: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/show-auth-grid`,
  KILL_SWITCH: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/kill-switch`,
  REFRESH_MEDICAL_PA_ORDERS: `${ApiBaseUrls.PA_ORDER_CREATION_SERVICE}medical/core-medical-pa/start-medical-pa`,
  GET_AUTH_GRID_SOURCE: `${ApiBaseUrls.BASE_URL}ai-service/medical/auth-grid/get-auth-grid-source`,
  SEND_FAX_FROM_CONFIG: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/send-fax-from-config`,
  GET_FAX_STATUS: `${ApiBaseUrls.BACKEND_SERVICE}commons/fax/get-fax-status`,
  GET_FORM_NAME: `${ApiBaseUrls.EV_BV_SERVICE}priority-health/get-form-name`,
  GET_PHARMA_STP_FILE_DATA: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/get-data-from-bigquery`,
  BIGQUERY_SEARCH_LIKE: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/search-like`,
  GET_FILENAME_SUMMARY: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/filename-summary`,
  UPDATE_BIGQUERY_DATA: `${ApiBaseUrls.BACKEND_SERVICE}commons/bigquery/update-data-to-bigquery`,
  PUSH_FILES_TO_SFTP: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/nycbs/push-files-to-sftp`,
  GET_FILE_FROM_NYCBS_SFTP: `${PHARMACY_PA_ORDER_CREATION_SERVICE}nycbs/get-file-from-nycbs-sftp`,
  BULK_SUBMIT_PA: `${PHARMACY_PA_ORDER_CREATION_SERVICE}bulk-pa/bulk-submit-pa`,
  RUN_NYCBS_PHARMA_STATUS_TRACKING: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}run_status_tracking`,
  BATCH_TEXT_NOTES_FROM_FILENAME: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}batch-text-notes-from-filename`,
  SUBMIT_CASE_INITIATION: RPA_NYCBS_PHARMA_SUBMIT_PA,
  EXPORT_TO_CSV: `${BACKEND_SERVICE}commons/bigquery/export-to-csv`,
  RUN_STATUS_TRACKING_BATCH: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}run_status_tracking_batch`,
  GET_PRIMARY_CARE_ORDERS: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/primary-care/get-orders`,
  SEARCH_PRIMARY_CARE: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/primary-care/search`,
  UPDATE_PRIMARY_CARE_ENTRY: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/primary-care/update-entry`,
  BIGQUERY_TABLE_PROFILE: `${BACKEND_SERVICE}commons/bigquery/table-profile`,
  EVALUATE_FIRST_STP_REVIEW: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/pa-order-update-with-rules/run`,
  MERGE_PDFS: `${ApiBaseUrls.BACKEND_SERVICE}commons/pdf-operations/merge-pdfs`,
  DRUG_SEARCH: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/drug-search/search`,
  RUN_PA_ORDER_UPDATE_WITH_RULES: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/pa-order-update-with-rules/run`,
  UPLOAD_ONCOEMR_BATCH: `${PHARMACY_PA_ORDER_CREATION_SERVICE}nycbs/upload-oncoemr-batch`,
  INITIATE_NYCBS_PA_BATCH: `${PHARMACY_PA_ORDER_CREATION_SERVICE}nycbs/initiate-nycbs-pa-batch`,
  PUSH_FILE_TO_NYCBS_SFTP: `${PHARMACY_PA_ORDER_CREATION_SERVICE}nycbs/push-file-to-nycbs-sftp`,
  UPLOAD_ONCOLOGY_PA_CSV: `${PHARMACY_PA_ORDER_CREATION_SERVICE}astera/upload-oncology-pa-csv`,
  // Pharmacy RPA service — batch / filtered submission pipeline trigger
  RUN_SUBMISSION_PIPELINE: `${ApiBaseUrls.PHARMACY_RPA_SERVICE}delivery/run-submission-pipeline`,
  INITIATE_BIOSIMILARS: `${ApiBaseUrls.RPA_SERVICE_KUBERNATES}initiate-biosimilars`,
  GENERATE_APPEAL: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/appeal/generate`,
  SEND_APPEAL: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/appeal/send`,
  APPEAL_NOT_REQUIRED: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/appeal/not-required`,
  APPEAL_LIST: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/appeal/list`,
  APPEAL_UPDATE: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/appeal/update`,
  APPEAL_DETAIL: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/appeal/detail`,
  GET_SUPPORT_TICKETS: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/support-tickets/get-tickets`,
  CREATE_SUPPORT_TICKET: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/support-tickets/create`,
  GET_SUPPORT_TICKET_DETAILS: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/support-tickets/get-details`,
  SEARCH_SUPPORT_TICKETS: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/support-tickets/search`,
  ADD_SUPPORT_TICKET_COMMENT: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/support-tickets/add-comment`,
  UPDATE_SUPPORT_TICKET: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/support-tickets/update`,
  ADD_SUPPORT_TICKET_ATTACHMENT: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/support-tickets/add-attachment`,
  QUESTIONNAIRE_REVIEW: `${ApiBaseUrls.BACKEND_SERVICE}pharmacy/questionnaire-review/`,
  MN_JUDGE_ASSESSMENT: `${ApiBaseUrls.BASE_URL}ai-service/pharmacy/mn-judge/assessment`,
  MN_JUDGE_ASSESS: `${ApiBaseUrls.BASE_URL}ai-service/pharmacy/mn-judge/assess`,
};
